import { isWebDocumentOrigin } from 'Utils/isWebDocumentOrigin'
import { getPaymentType } from './paymentTypesValidation'

const PENDING_PAYMENT_METHODS = new Set(['PENDIENTEPAGO', 'PENDIENTEDEPAGO'])
const AMOUNT_TOLERANCE = 0.01

const normalizePaymentMethod = value =>
  String(value || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')

const isPendingPaymentMethod = value =>
  PENDING_PAYMENT_METHODS.has(normalizePaymentMethod(value))

// Checks PaymentName and PaymentTypeId fields; SearchSalesDocumentDetail
// doesn't always populate PaymentName.
const matchesPaymentField = (payment, substring) =>
  [
    payment?.PaymentName,
    payment?.PaymentTypeId,
    payment?.PaymentMethodDetails?.PaymentTypeId,
  ].some(value => normalizePaymentMethod(value).includes(substring))

const isCommercialCreditPayment = payment =>
  Boolean(getPaymentType(payment).isCreditAccount) ||
  matchesPaymentField(payment, 'COMERCIAL')

const isContadoPayment = payment => matchesPaymentField(payment, 'CONTADO')

export const hasPendingOrEmptyPaymentMethod = paymentMethod => {
  if (
    paymentMethod == null ||
    paymentMethod === '' ||
    (Array.isArray(paymentMethod) && paymentMethod.length === 0)
  ) {
    return true
  }

  const paymentMethods = Array.isArray(paymentMethod)
    ? paymentMethod
    : [paymentMethod]

  return paymentMethods.some(payment => {
    if (typeof payment === 'string') {
      return isPendingPaymentMethod(payment)
    }

    const paymentMethodCandidates = [
      payment?.PaymentName,
      payment?.PaymentTypeId,
      payment?.PaymentMethodId,
      payment?.PaymentMethodDetails?.PaymentTypeId,
      payment?.PaymentMethodDetails?.PaymentMethodId,
    ]

    return (
      paymentMethodCandidates.some(isPendingPaymentMethod) ||
      Object.keys(payment || {}).some(isPendingPaymentMethod)
    )
  })
}

/**
 * A web/LITA order's payment amount is a snapshot, not derived live from
 * the total, so it goes stale if the order is edited afterward. Builds the
 * resync payload when that happens - only for a single, unambiguous
 * Crédito Comercial or Contado payment, since that's all
 * updateOdvPaymentMethod can represent (no splits, no partial amounts).
 */
export const getStalePaymentResyncPayload = ({
  Origin,
  DocHeaderId,
  AmountWithTax,
  Payments,
  PaymentMethod,
} = {}) => {
  if (!isWebDocumentOrigin(Origin)) {
    return null
  }

  if (hasPendingOrEmptyPaymentMethod(PaymentMethod ?? Payments)) {
    return null
  }

  if (!Array.isArray(Payments) || Payments.length !== 1) {
    return null
  }

  const [payment] = Payments
  const isCommercialCredit = isCommercialCreditPayment(payment)
  const isContado = isContadoPayment(payment)

  if (!isCommercialCredit && !isContado) {
    return null
  }

  // Number(null/'') is 0, indistinguishable from a real $0 total, so reject
  // a missing amount explicitly instead of resyncing the payment to $0.
  if (AmountWithTax == null || AmountWithTax === '') {
    return null
  }

  const newAmount = Number(AmountWithTax)
  if (!Number.isFinite(newAmount)) {
    return null
  }

  const currentAmount = Number(payment?.PaymentAmount) || 0

  if (Math.abs(currentAmount - newAmount) < AMOUNT_TOLERANCE) {
    return null
  }

  // SearchSalesDocumentDetail echoes these back under PaymentMethodDetails,
  // not at the top level.
  const creditAccountNumber =
    payment?.PaymentMethodDetails?.PaymentReferenceNumber ??
    payment?.PaymentReferenceNumber
  const orderNumber =
    payment?.PaymentMethodDetails?.OrderNumber ?? payment?.OrderNumber

  if (isCommercialCredit && !creditAccountNumber) {
    // A CreditoComercial payload without an account reference 500s -
    // better to leave it stale than submit a request known to fail.
    return null
  }

  return {
    saleOrderId: DocHeaderId,
    isCommercialCredit,
    amount: newAmount,
    orderNumber: isCommercialCredit ? orderNumber ?? null : null,
    creditAccount: isCommercialCredit
      ? { CreditAccountNumber: creditAccountNumber }
      : null,
  }
}
